import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'

import { PAGE_DEFAULT_VALUE } from '../../../constants/route'
import { ScreenContainerRef } from '../../../context'
import { useUk } from '../../../hooks'
import { updatePaginationPage } from '../../../utils/pagination'
import { PageActions } from '../../Page'
import {
  getTableResetSortingButton,
  TableControlBar,
  TableControlButtons,
  useTableRefresh,
} from '../../Table'
import {
  useRequestTableAttachedRequests,
  useRequestTableDocFlows,
} from '../Request.hooks'
import { getItemActions } from '../Request.utils.actions'
import {
  RequestBoard,
  useRequestBoard,
  useRequestBoardChangeStatus,
  useRequestBoardItemReload,
} from '../RequestBoard'

import * as styles from './RequestBoardLayout.module.css'

function RequestBoardLayout({
  ChangeViewTypeButton = null,
  changeViewTypeButtonProps,
  emptyState = null,
  extraModals = null,
  getProfileLink,
  hasTable,
  modal = null,
  pendingIds,
  sub,
  tablePage,
  onClickCard,
  onCloseModal,
  onMouseDownCard,
  onRegisterItemReload,
  onRegisterReload,
  onReloadList,
  onSetModal,
  onSetPendingIds,
}) {
  const {
    canConnectDocFlow,
    canCreateDocFlow,
    filterValuesWithExtra,
    handleChangeExtraEnabled,
    handleCleanSelection,
    handleClickEmailTag,
    handleClickFileTag,
    handleReload: handleReloadTable,
    handleSetPagination,
    isDense,
    isLoadedInit,
    isLoading,
    isPerformingMassAction,
    ordering,
    pagination,
    setItemAction,
  } = tablePage

  const { t } = useTranslation()

  const { config: ukConfig } = useUk()

  const board = useRequestBoard({
    filterValues: filterValuesWithExtra,
    isEnabled: isLoadedInit,
    onRegisterReload,
  })

  const { handleCallAction } = useRequestBoardItemReload({
    isPerformingMassAction,
    onCallAction: setItemAction,
    onRegisterItemReload,
    onReload: board.handleReload,
    onReloadItem: board.handleReloadItem,
    onStartAction: board.handleStartItemAction,
  })

  const {
    draggedStatus,
    dropStatus,
    handleDragCancel,
    handleDragEnd,
    handleDragOver,
    handleDragStart,
  } = useRequestBoardChangeStatus({
    columns: board.columns,
    filterValues: filterValuesWithExtra,
    sub,
    onReloadList,
    onReloadTable: handleReloadTable,
    onSetPendingIds,
  })

  async function handleReloadBoardAndTable() {
    handleReloadTable()

    return board.handleReload()
  }

  const { handleClickLinkedRequestTag, tagModal } =
    useRequestTableAttachedRequests({
      requests: { items: board.items },
      onCloseModal,
      onReload: handleReloadBoardAndTable,
      onSetModal,
    })

  const { docFlowTagModal, handleClickDocFlowTag } = useRequestTableDocFlows({
    onCloseModal,
    onReload: handleReloadBoardAndTable,
    onSetModal,
  })

  const { refreshButtonProps } = useTableRefresh({
    isRefetching: board.isLoading,
    onReload: handleReloadBoardAndTable,
  })

  // The board shows neither the table's page nor its tags: an emptied URL page
  // would hide it behind the empty state, and the tags would load for nothing.
  useEffect(() => {
    handleCleanSelection()

    handleSetPagination(updatePaginationPage(pagination, PAGE_DEFAULT_VALUE), {
      replace: true,
    })

    handleChangeExtraEnabled({ isEnabled: false })

    return () => {
      handleChangeExtraEnabled({ isEnabled: true })
    }
  }, [])

  function getActions(item) {
    return getItemActions({
      canConnectDocFlow,
      canCreateDocFlow,
      item,
      t,
      ukConfig,
    })
  }

  return (
    <>
      {hasTable ? (
        <>
          <TableControlBar>
            <PageActions className={styles.pageActions} isLoading={isLoading}>
              <TableControlButtons
                keepChangeViewTypeButtonOnNarrowScreen
                ChangeViewTypeButton={ChangeViewTypeButton}
                ResetSortingButton={getTableResetSortingButton(ordering)}
                changeViewTypeButtonProps={changeViewTypeButtonProps}
                configButtonProps={{
                  disabled: true,
                  tooltip: 'Table.ViewRegimeLockedTooltipKey',
                }}
                densityButtonProps={{
                  disabled: true,
                  isDense,
                  tooltip: 'Table.ViewRegimeLockedTooltipKey',
                }}
                refreshButtonProps={refreshButtonProps}
                resetSortingProps={{
                  disabled: true,
                  tooltip: 'Table.ViewRegimeLockedTooltipKey',
                }}
              />
            </PageActions>
          </TableControlBar>
          <RequestBoard
            cardProps={{
              extra: board.extra,
              getActions,
              getProfileLink,
              onCallAction: handleCallAction,
              onClick: onClickCard,
              onClickDocFlowTag: handleClickDocFlowTag,
              onClickEmailTag: handleClickEmailTag,
              onClickFileTag: handleClickFileTag,
              onClickRequestTag: handleClickLinkedRequestTag,
              onMouseDown: onMouseDownCard,
            }}
            columns={board.columns}
            draggedStatus={draggedStatus}
            dropStatus={dropStatus}
            pendingIds={pendingIds}
            onDragCancel={handleDragCancel}
            onDragEnd={handleDragEnd}
            onDragOver={handleDragOver}
            onDragStart={handleDragStart}
          />
        </>
      ) : (
        emptyState
      )}
      <ScreenContainerRef value={null}>
        {tagModal}
        {docFlowTagModal}
        {modal}
        {extraModals}
      </ScreenContainerRef>
    </>
  )
}

export default RequestBoardLayout
